import React, { useEffect, createElement, Component } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Mail, MapPin, Clock, ShieldCheck, CheckCircle, ArrowRight, FileCheck } from 'lucide-react';
import { motion } from 'framer-motion';
const EMAIL = 'info@uk-medicolegal-assessment.com';
const SUBJECT = encodeURIComponent('New Website Enquiry');
const BODY = encodeURIComponent(`Dear UK Medicolegal Assessment,

I would like to enquire about your medico-legal reporting services. Please find my details and case information below:

Full Name: 
Organisation / Law Firm: 
Role / Position: 
Email Address: 
Case Type (e.g. Personal Injury, Clinical Negligence, Family Court, Criminal, Immigration, Industrial Disease): 
Brief Case Summary: 
Claimant Name (if known): 
Specialty Required (e.g. Psychiatry, Orthopaedics, ENT, GP, Neurology): 
Urgency / Preferred Timescale: 
Location Preference for Assessment: 
Any Additional Information or Specific Questions for the Expert: 

I look forward to hearing from you.

Kind regards,
`);
const MAILTO_LINK = `mailto:${EMAIL}?subject=${SUBJECT}&body=${BODY}`;
export function ContactPage() {
  useEffect(() => {
    document.title = 'Contact Us | Instruct an Expert | UK Medicolegal Assessment';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Instruct a medico-legal expert today. Email info@uk-medicolegal-assessment.com for a fee quote within 24 hours. Independent, CPR Part 35 compliant reports. UK-wide coverage.');
    setMeta('robots', 'index, follow');
  }, []);
  return <div className="min-h-screen bg-warm-50" data-id="element-700">
      <Navigation data-id="element-701" />

      {/* Hero Section */}
      <section className="relative bg-cta-500 pt-32 pb-20 lg:pt-40 lg:pb-24" data-id="element-702">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10" data-id="element-703">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="text-center max-w-3xl mx-auto" data-id="element-704">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight" data-id="element-705">
              Instruct an Expert
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed" data-id="element-706">
              Send us your enquiry and we'll respond within 24 hours during
              business hours. Mark "Urgent" in the subject for priority
              response.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main CTA Section */}
      <section className="py-16 md:py-24" data-id="element-707">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-708">
          <div className="card text-center" data-id="element-709">
            <div className="w-20 h-20 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-8" data-id="element-710">
              <Mail className="h-10 w-10 text-teal-600" data-id="element-711" />
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-ink-900 tracking-tight mb-4" data-id="element-712">
              Send Us Your Enquiry
            </h2>
            <p className="text-ink-500 leading-relaxed mb-3 max-w-lg mx-auto" data-id="element-713">
              Click the button below to open your email app. We've pre-filled a
              template with helpful prompts so you can give us the details we
              need to recommend the right expert and provide a fee quote.
            </p>
            <p className="text-lg font-bold text-teal-600 mb-8" data-id="element-714">{EMAIL}</p>

            <a href={MAILTO_LINK} className="btn-primary w-full sm:w-auto text-center text-lg py-4 px-10" data-id="element-715">
              <Mail className="mr-2.5 h-5 w-5" data-id="element-716" />
              Open Email & Send Enquiry
            </a>

            <p className="text-sm text-ink-400 mt-6 leading-relaxed max-w-md mx-auto" data-id="element-717">
              Your default email application will open with our address, subject
              line, and a template pre-filled. Simply complete the details and
              hit send.
            </p>
          </div>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="pb-16 md:pb-24" data-id="element-718">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-719">
          <h2 className="text-2xl md:text-3xl font-bold text-ink-900 tracking-tight mb-10 text-center" data-id="element-720">
            What Happens Next
          </h2>
          <div className="space-y-6" data-id="element-721">
            {[{
            step: '01',
            title: 'We receive your enquiry',
            description: 'Our team reviews your case details and identifies the most suitable expert from our panel.'
          }, {
            step: '02',
            title: 'Fee quote & expert recommendation',
            description: "Within 24 hours during business hours, we'll respond with a competitive fee quote and a recommended specialist. Mark 'Urgent' in the subject for priority response."
          }, {
            step: '03',
            title: 'Instruction confirmed',
            description: 'Once you confirm, we arrange the assessment and keep you updated throughout the process.'
          }].map(item => <div key={item.step} className="flex items-start gap-5" data-id="element-722">
                <div className="w-12 h-12 rounded-xl bg-cta-500 text-white flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-723">
                  {item.step}
                </div>
                <div data-id="element-724">
                  <h3 className="text-lg font-bold text-ink-900 mb-1" data-id="element-725">
                    {item.title}
                  </h3>
                  <p className="text-ink-500 leading-relaxed" data-id="element-726">
                    {item.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Contact Details */}
      <section className="py-16 md:py-24 bg-white border-t border-warm-200" data-id="element-727">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-728">
          <h2 className="text-2xl md:text-3xl font-bold text-ink-900 tracking-tight mb-10 text-center" data-id="element-729">
            Contact Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-730">
            <div className="flex items-start space-x-4" data-id="element-731">
              <div className="bg-teal-50 p-3.5 rounded-xl flex-shrink-0" data-id="element-732">
                <Mail className="h-6 w-6 text-teal-600" data-id="element-733" />
              </div>
              <div data-id="element-734">
                <h3 className="font-bold text-ink-900 mb-2" data-id="element-735">Email Us</h3>
                <a href={MAILTO_LINK} className="text-teal-600 hover:text-teal-700 block text-sm font-semibold transition-colors" data-id="element-736">
                  {EMAIL}
                </a>
              </div>
            </div>

            <div className="flex items-start space-x-4" data-id="element-737">
              <div className="bg-teal-50 p-3.5 rounded-xl flex-shrink-0" data-id="element-738">
                <Clock className="h-6 w-6 text-teal-600" data-id="element-739" />
              </div>
              <div data-id="element-740">
                <h3 className="font-bold text-ink-900 mb-2" data-id="element-741">Response Time</h3>
                <p className="text-sm text-ink-500 leading-relaxed" data-id="element-742">
                  Within 24 hours during business hours
                  <br data-id="element-743" />
                  Mon–Fri, 9am–5pm
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4" data-id="element-744">
              <div className="bg-teal-50 p-3.5 rounded-xl flex-shrink-0" data-id="element-745">
                <MapPin className="h-6 w-6 text-teal-600" data-id="element-746" />
              </div>
              <div data-id="element-747">
                <h3 className="font-bold text-ink-900 mb-2" data-id="element-748">Coverage</h3>
                <p className="text-sm text-ink-500 leading-relaxed" data-id="element-749">
                  UK-wide service
                  <br data-id="element-750" />
                  England, Wales, Scotland
                  <br data-id="element-751" />& Northern Ireland
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3" data-id="element-752">
            <div className="flex items-center text-sm font-medium text-ink-700" data-id="element-753">
              <ShieldCheck className="h-4 w-4 mr-2 text-sage-500" data-id="element-754" />
              ISO 27001 Certified
            </div>
            <div className="flex items-center text-sm font-medium text-ink-700" data-id="element-755">
              <CheckCircle className="h-4 w-4 mr-2 text-sage-500" data-id="element-756" />
              GDPR Compliant
            </div>
            <div className="flex items-center text-sm font-medium text-ink-700" data-id="element-757">
              <FileCheck className="h-4 w-4 mr-2 text-sage-500" data-id="element-758" />
              CPR Part 35 Compliant
            </div>
          </div>
        </div>
      </section>

      <Footer data-id="element-759" />
    </div>;
}